import { Link, useLocation } from 'wouter';
import {
  Building2,
  Users,
  LayoutDashboard,
  Settings,
  QrCode,
  ScanLine,
  Calendar,
  Bell,
  ClipboardList,
  FileText,
  Package,
  UsersRound,
  Database,
  ShieldCheck,
  HardHat,
  type LucideIcon,
} from 'lucide-react';
import { useAuth, type FeatureKey } from '@/auth/AuthContext';
import { useSelectedSite } from '@/auth/useSelectedSite';
import { useViewMode } from '@/auth/useViewMode';
import { cn, initials } from '@/lib/utils';
import { MobileTabBar } from './MobileTabBar';
import { SiteSwitcher } from './SiteSwitcher';

interface NavItem {
  label: string;
  href: string;
  icon: LucideIcon;
  feature?: FeatureKey;
}

const baseNav: NavItem[] = [{ label: 'Home', href: '/', icon: LayoutDashboard }];

const adminNav: NavItem[] = [
  { label: 'Sites', href: '/admin/sites', icon: Building2 },
  { label: 'Checkpoints', href: '/admin/checkpoints', icon: QrCode },
  { label: 'Users', href: '/admin/users', icon: Users },
  { label: 'Census', href: '/admin/census', icon: Database },
  { label: 'Settings', href: '/admin/settings', icon: Settings },
];

// Hot Box is intentionally not its own entry — it lives as a tab on the Storage page.
const fieldNavFor = (slug: string): NavItem[] => [
  { label: 'Scan', href: `/sites/${slug}/scan`, icon: ScanLine, feature: 'scan' },
  { label: 'Tours', href: `/sites/${slug}/tours`, icon: Calendar, feature: 'tours' },
  { label: 'Rounding', href: `/sites/${slug}/rounding`, icon: ClipboardList, feature: 'rounding' },
  { label: 'DAR', href: `/sites/${slug}/dar`, icon: FileText, feature: 'dar' },
  { label: 'Storage', href: `/sites/${slug}/storage`, icon: Package, feature: 'storage' },
  { label: 'Census', href: `/sites/${slug}/census`, icon: UsersRound, feature: 'census' },
  { label: 'Alerts', href: `/sites/${slug}/alerts`, icon: Bell, feature: 'alerts' },
];

export function AppShell({ children }: { children: React.ReactNode }) {
  const { me } = useAuth();
  const { accessibleSites, selectedSite, selectedSlug, selectSlug } = useSelectedSite();
  const { viewMode, toggleViewMode, canSwitchView } = useViewMode();

  const fieldItems = selectedSite
    ? fieldNavFor(selectedSite.slug).filter(
        (i) => !i.feature || selectedSite.features[i.feature],
      )
    : [];

  const showAdminSection = canSwitchView && viewMode === 'admin';

  return (
    <div className="flex h-full bg-background text-foreground">
      <aside className="hidden md:flex md:w-60 lg:w-64 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground">
        <Link
          href="/"
          className="flex items-center gap-3 px-4 py-4 border-b border-sidebar-border hover-elevate"
        >
          <img
            src="/ctc-avatar.jpg"
            alt="Circle the City"
            className="h-10 w-10 rounded-md object-contain bg-white"
          />
          <div className="flex flex-col leading-tight">
            <span className="text-sm font-semibold">CTC Security</span>
            <span className="text-[11px] text-muted-foreground mt-0.5">Operations</span>
          </div>
        </Link>

        <div className="px-3 pt-3">
          <SiteSwitcher
            sites={accessibleSites}
            selectedSlug={selectedSlug}
            onSelect={selectSlug}
            variant="sidebar"
          />
        </div>

        <nav className="flex-1 overflow-y-auto px-2 py-3">
          <SidebarSection items={baseNav} />

          {fieldItems.length > 0 && (
            <div className="mt-3">
              <SidebarLabel>{selectedSite?.name ?? 'Site'}</SidebarLabel>
              <SidebarSection items={fieldItems} />
            </div>
          )}

          {showAdminSection && (
            <div className="mt-3">
              <SidebarLabel>Administration</SidebarLabel>
              <SidebarSection items={adminNav} />
            </div>
          )}
        </nav>

        {canSwitchView && (
          <div className="px-3 pb-2">
            <ViewModeToggle viewMode={viewMode} onToggle={toggleViewMode} />
          </div>
        )}

        {me && (
          <div className="border-t border-sidebar-border px-3 py-3">
            <Link
              href="/me/preferences"
              className="flex items-center gap-2.5 px-1 py-1 -my-1 rounded-md hover-elevate"
              title="My preferences"
            >
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-[11px] font-semibold text-foreground">
                {initials(me.user.displayName)}
              </div>
              <div className="min-w-0 leading-tight">
                <div className="truncate text-sm font-medium">{me.user.displayName}</div>
                <div className="truncate text-xs text-muted-foreground">{me.user.email}</div>
              </div>
            </Link>
          </div>
        )}
      </aside>

      <main className="flex-1 overflow-y-auto pb-16 md:pb-0">{children}</main>
      <MobileTabBar />
    </div>
  );
}

export function ViewModeToggle({
  viewMode,
  onToggle,
  variant = 'sidebar',
}: {
  viewMode: 'admin' | 'user';
  onToggle: () => void;
  variant?: 'sidebar' | 'sheet';
}) {
  const Icon = viewMode === 'admin' ? ShieldCheck : HardHat;
  const label = viewMode === 'admin' ? 'Admin view' : 'User view';
  const hint =
    viewMode === 'admin'
      ? 'Switch to user view to preview what guards see'
      : 'Currently previewing as a guard — switch back to admin';
  return (
    <button
      type="button"
      onClick={onToggle}
      title={hint}
      className={cn(
        'w-full flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs hover-elevate transition-colors',
        variant === 'sidebar'
          ? 'border-sidebar-border bg-background/30'
          : 'border-input bg-background',
        viewMode === 'admin' ? 'text-foreground' : 'text-primary',
      )}
    >
      <div className="flex items-center gap-2 min-w-0">
        <Icon className="h-3.5 w-3.5 shrink-0" />
        <span className="font-medium">{label}</span>
      </div>
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
        Switch
      </span>
    </button>
  );
}

function SidebarLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 px-3 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
      {children}
    </div>
  );
}

function SidebarSection({ items }: { items: NavItem[] }) {
  const [location] = useLocation();
  const allHrefs = items.map((i) => i.href);
  return (
    <ul className="space-y-0.5">
      {items.map((item) => {
        const Icon = item.icon;
        const active = isActiveHref(item.href, location, allHrefs);
        return (
          <li key={item.href}>
            <Link
              href={item.href}
              className={cn(
                'group relative flex items-center gap-2.5 rounded-md px-3 py-2 text-sm transition-colors hover-elevate',
                active
                  ? 'bg-sidebar-accent text-foreground font-medium'
                  : 'text-foreground/75',
              )}
            >
              {active && (
                <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-full bg-primary" />
              )}
              <Icon
                className={cn(
                  'h-4 w-4 shrink-0',
                  active ? 'text-primary' : 'text-muted-foreground',
                )}
              />
              {item.label}
            </Link>
          </li>
        );
      })}
    </ul>
  );
}

// Highlight the longest-matching href among siblings so a nav item like
// /sites/dmrc/storage doesn't also light up when the user is on
// /sites/dmrc/storage/temp (which has its own Hot Box entry).
function isActiveHref(href: string, location: string, allHrefs: string[]): boolean {
  if (location === href) return true;
  if (href === '/') return false;
  if (!location.startsWith(href + '/')) return false;
  const moreSpecific = allHrefs.some(
    (h) => h !== href && h.startsWith(href + '/') && (location === h || location.startsWith(h + '/')),
  );
  return !moreSpecific;
}
